import { GitType } from "@/enum/project.enum";
import { object, string, number, array, boolean, mixed } from "yup";
// create a yup custom url validator
const urlRegex = new RegExp(
  "^((?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\\.)+[a-z0-9][a-z0-9-]{0,61}[a-z0-9])$",
);

const username = string()
  .required("Please provide a username.")
  .min(3)
  .max(20)
  .matches(
    /^[a-zA-Z0-9_]+$/,
    "Oops! Your username can only include letters, numbers, and underscores. Please adjust it accordingly.",
  );
const email = string()
  .email("Invalid email")
  .required(
    "Oops! Looks like you missed entering your email. It's a required field!",
  );

const userSetting = object().shape({
  names: string().required(
    "It looks like you missed entering your name. Can you fill it in?",
  ),
  email,
  username,
});

const login = object({ email });

const register = login.shape({
  first_name: string().required("Please provide your first name."),
  last_name: string().optional(),
  username,
  company: string().optional(),
});

const projectConfig = object().shape({
  git: string().required().oneOf(Object.values(GitType)),
  framework: string().required(),
  healthCheckPath: string()
    .optional()
    .test(
      "starts-with-slash",
      "Path must start with a forward slash",
      (value) => {
        if (!value) return true;
        return value.startsWith("/");
      },
    ),
  repo: object({
    name: string().required(),
    branch: string().when("git", {
      is: GitType.Docker,
      then: string().optional(),
      otherwise: string().required("Branch is required"),
    }),
  }),
  authEnabled: boolean().default(false),
  rootDirectory: string(),
  buildCommand: string(),
  outputDirectory: string(),
  branch: string().when("git", {
    is: GitType.Docker,
    then: string().optional(),
    otherwise: string().required("Branch is required"),
  }),
  installationId: number(),
  teamId: string(),
  environments: array().of(
    object({
      name: string(),
      value: string(),
      id: string(),
    }),
  ),
  configurations: object()
    .shape({
      cpu: number().required().min(0.2),
      memory: number().required().min(0.5),
      storage: number().required().min(1),
      region: object()
        .shape({
          _id: string().required(),
          name: string().required(),
          country: string().required(),
          continent: string().required(),
          enabled: boolean().required(),
        })
        .required("Region is required"),
    })
    .required("Configuration is required"),
});

const dockerImageConfig = object().shape({
  image: string()
    .required("Docker image URL is required")
    .matches(
      /^([a-z0-9]+(?:[._-][a-z0-9]+)*\/)*[a-z0-9]+(?:[._-][a-z0-9]+)*:[a-z0-9]+(?:[._-][a-z0-9]+)*$|^[a-z0-9]+(?:[._-][a-z0-9]+)*$/,
      "Invalid Docker image format. Please use one of the example formats shown.",
    ),
  registry: object().shape({
    id: string().required(),
    name: string().required(),
  }),
  username: string().optional(),
  token: string().when("username", {
    is: (username: string) => username && username.length > 0,
    then: string().required(
      "Access token is required when username is provided",
    ),
    otherwise: string().optional(),
  }),
});

const domain = object().shape({
  name: string()
    .required("Just a friendly reminder - we need the domain name to proceed.")
    .test("is-url", "is invalid, Please enter a valid domain name", (value) => {
      if (!value) return false;
      return urlRegex.test(value);
    }),
});

const newTeam = object().shape({
  team_name: string()
    .min(3)
    .max(16)
    .required("Team is required")
    .label("Team Name"),
  description: string(),
  image: string(),
  specifications: object({
    members: number().required("Number of members is required"),
    slack_support: boolean(),
    concurrent_builds: number(),
  }),
  members: array().of(string()),
  card_id: string(),
});

const inviteMember = object().shape({
  members: array().of(object()).min(1).required().label("Member"),
});

const databaseConfig = object().shape({
  engine: string().required(),
  name: string().required(),
  region: string().required(),
  memory: number().required(),
  storage: number().required(),
  cpu: number().required(),
});

const updateDatabaseConfig = object().shape({
  name: string().required(),
  memory: number().required(),
  storage: number().required(),
  cpu: number().required(),
  ips: array().of(
    object().shape({
      id: string().required(),
      ip: string()
        .required()
        .matches(
          /^((25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\/([0-9]|[1-2][0-9]|3[0-2])$/,
          "IP address must be in CIDR format with prefix (e.g., 100.105.22.30/32)",
        )
        .test(
          "cidr-required",
          "CIDR prefix is required (e.g., 100.105.22.30/32)",
          (value) => (value ? value.includes("/") : false),
        ),
    }),
  ),
});
const validation = {
  userSetting,
  login,
  register,
  projectConfig,
  domain,
  newTeam,
  inviteMember,
  databaseConfig,
  updateDatabaseConfig,
  dockerImageConfig,
};

export default validation;
